import { useIsMutating } from '@tanstack/react-query';
import { Button } from '@optimus-web/core';
import { FloatingPanel } from '@mews/b2b-ui';
import { getRepositoryQueryKey, RoleGetResult } from '@mews-commander/data-access';
import { LocalizedText, useDialog } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { UnsavedChangesDialog, OptimusProvider } from '@mews-commander/ui';
import { useRoleContext } from '../context';
import { EditRoleForm } from './EditRoleForm';
import { useEditRoleForm } from './useEditRoleForm';

interface EditRoleDialogProps {
    role: RoleGetResult;
    onClose: () => void;
    active: boolean;
}

export const EditRoleDialog = ({ role, onClose, active }: EditRoleDialogProps) => {
    const [{ active: showUnsavedChangesDialog }, toggleUnsavedChangesDialog] = useDialog();

    const { activeRole, openRoleDetail } = useRoleContext();

    const { renderFormContent, submit, isLoading, hasDirtyFields, reset } = useEditRoleForm({
        role,
        onSuccess: (data) => {
            const updatedRole = data.roles?.[0];

            if (activeRole && activeRole.id === updatedRole?.id) {
                openRoleDetail({ role: updatedRole || null });
            }

            onClose();
        },
    });

    const unassigningUsers = useIsMutating({
        mutationKey: getRepositoryQueryKey('roles', 'assignUsers'),
        exact: false,
    });

    const handleClose = () => {
        if (hasDirtyFields) {
            toggleUnsavedChangesDialog();
        } else {
            onClose();
        }
    };

    return renderFormContent(
        <OptimusProvider density="spacious">
            <FloatingPanel
                active={active}
                loading={isLoading || unassigningUsers > 0}
                footer={{
                    primary: (
                        <Button type="submit" variant="primary" onClick={submit}>
                            <LocalizedText textKey={TextKey.SaveChanges} />
                        </Button>
                    ),
                }}
                title={<LocalizedText textKey={TextKey.RoleEditTitle} substitutes={{ roleName: role.name }} />}
                onClose={handleClose}
            >
                <EditRoleForm />
            </FloatingPanel>
            <UnsavedChangesDialog
                content={
                    <LocalizedText
                        textKey={TextKey.RoleEditUnsavedChangesDescription}
                        substitutes={{ roleName: role.name }}
                    />
                }
                active={showUnsavedChangesDialog}
                onCancel={toggleUnsavedChangesDialog}
                onDiscardChanges={() => {
                    onClose();
                    toggleUnsavedChangesDialog();
                    reset();
                }}
            />
        </OptimusProvider>
    );
};
